import type { AuthTokens } from '@cf/shared';

/** Error body returned by the API (see AllExceptionsFilter). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<AuthTokens | null> | null = null;
let onSessionLost: (() => void) | null = null;

export const session = {
  set(token: string | null) {
    accessToken = token;
  },
  token(): string | null {
    return accessToken;
  },
  onLost(cb: () => void) {
    onSessionLost = cb;
  },
};

async function parse(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  return text ? (JSON.parse(text) as unknown) : undefined;
}

function toError(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as { code?: string; message?: string; details?: unknown };
  return new ApiError(status, b.code ?? 'HTTP_ERROR', b.message ?? `HTTP ${status}`, b.details);
}

/**
 * Exchanges the httpOnly refresh cookie for a new access token.
 * Single-flight: concurrent 401s share one refresh request (the API rotates tokens).
 */
export function refreshSession(): Promise<AuthTokens | null> {
  refreshing ??= fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' })
    .then(async (res) => {
      if (!res.ok) return null;
      const tokens = (await parse(res)) as AuthTokens;
      accessToken = tokens.accessToken;
      return tokens;
    })
    .catch(() => null)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export async function api<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const doFetch = () =>
    fetch(`/api${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body: json !== undefined ? JSON.stringify(json) : rest.body,
    });

  let res = await doFetch();
  if (res.status === 401 && !path.startsWith('/auth/')) {
    const renewed = await refreshSession();
    if (!renewed) {
      onSessionLost?.();
      throw toError(401, await parse(res));
    }
    res = await doFetch();
  }
  const body = await parse(res);
  if (!res.ok) throw toError(res.status, body);
  return body as T;
}
